import classnames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import * as React from 'react';
import { Text } from '@/core/tamagoshi/Text/Text';
import { H3 } from '@/domains/core/tamagoshiv2/components/Typography/Typography';
import { useExpandedTable } from '@/domains/productDiscovery/ProductPage/common/components/Attributes/components/AttributesTable/hooks/useExpandedTable';
import { trimNumber } from '@/domains/productDiscovery/ProductPage/common/components/Attributes/components/AttributesTable/utils/trimDecimals';
import { type Attribute } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { CarbonScoreAttribute } from '@/domains/sustainability/carbonScore/components/CarbonScoreAttribute/CarbonScoreAttribute';
import { translateAttributeName } from './utils/translateAttributeName';
import styles from './attributes.module.scss';
type AttributesTableTheme = 'color' | 'gray';
export type AttributesTableColumns = 'name' | 'value';
type WeightType = Required<React.ComponentProps<typeof Text>>['weight'];
interface AttributesTableProps {
  id?: string;
  defaultExpanded?: boolean;
  attributes: Attribute[];
  labels?: {
    fold?: string;
    unfold: string;
  };
  maxRowsDisplayed?: number;
  title: React.ReactNode;
  theme?: AttributesTableTheme;
  columnsWeight?: Record<AttributesTableColumns, WeightType>;
  behavior?: 'unfold' | 'unfold-fold' | {
    type: 'action';
    action: () => unknown;
  };
  gtmProps?: {
    title: string;
  };
}
export const AttributesTable: React.FC<AttributesTableProps> = ({
  id,
  defaultExpanded = false,
  attributes,
  labels: {
    unfold,
    fold
  } = {},
  maxRowsDisplayed = 4,
  title,
  theme = 'color',
  columnsWeight,
  behavior = 'unfold',
  gtmProps
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isExpanded, onExpanded] = useExpandedTable(attributes, gtmProps?.title ?? '', defaultExpanded,
  // eslint-disable-next-line no-nested-ternary
  behavior === 'unfold' ? {
    type: 'unfold'
  } : behavior === 'unfold-fold' ? {
    type: 'unfold-fold',
    to: ref
  } : behavior);
  const toggleHidden = useCallback(() => {
    onExpanded?.(!isExpanded);
  }, [isExpanded, onExpanded]);
  const effectiveAttributes = useMemo(() => isExpanded ? attributes : attributes.slice(0, maxRowsDisplayed), [attributes, isExpanded, maxRowsDisplayed]);
  if (!effectiveAttributes.length) return null;
  const unfoldButtonLabel = !isExpanded ? unfold : fold;
  return <div id={id} className={classnames(styles.attributesContainer, theme === 'color' ? styles.color : styles.gray)} ref={ref}>
      <H3 className={styles.attributesTitle}>{title}</H3>
      <ul className={styles.list}>
        {React.Children.toArray(effectiveAttributes.map(attribute => <li className={styles.listItem}>
              <Text className={styles.name} weight={columnsWeight?.name ?? 'regular'} color="#0c193a">
                {translateAttributeName(attribute.name)}
                <CarbonScoreAttribute attributeId={attribute.id} />
              </Text>
              <Text className={styles.value} weight={columnsWeight?.value ?? 'regular'} color="#505971">
                {[trimNumber(attribute.value), attribute.unit].filter(Boolean).join(' ')}
              </Text>
            </li>))}
        {!defaultExpanded && attributes.length > maxRowsDisplayed + 1 && unfoldButtonLabel && <li className={classnames(styles.listItem, styles.listItemSeeMore)}>
              <Text className={styles.seeAllButton} onClick={toggleHidden}>
                {unfoldButtonLabel}
              </Text>
            </li>}
      </ul>
    </div>;
};